import { Inject, Injectable } from "@angular/core";
import { LocationWebshopViewModel } from "../../shared/models";
import { AddToCartPayload } from "./payloads/add-to-cart.payload";
import { TrackingPayload } from "./payloads/tracking-payload.interface";
import { Tracker } from "./tracker.interface";
import { TRACKER_TOKEN } from './tracker.token';
import { TrackingPayloads } from "./tracking-event-payloads";
import { TrackingEvent } from "./tracking-event.enum";
import { CompleteRegistrationPayload } from "./payloads/complete-registration.payload";
import { PurchasePayload } from "./payloads/purchase.payload";
import { AddPaymentInfoPayload } from "./payloads/add-payment-info.payload";
import { FindLocationPayload } from "./payloads/find-location.payload";
import { InitiateCheckoutPayload } from "./payloads/initiate-checkout.payload";
import { CoreLogService } from "../log.service";


@Injectable({ providedIn: "root" })
export class TrackingManager {
    constructor(@Inject(TRACKER_TOKEN) private trackers: Tracker[],
        private logger: CoreLogService) { }

    /**
     * Dispatches a tracking event to all active trackers.
     * 
     * - Wraps each tracker in a try/catch to prevent app crashes on error.
     * 
     * @param event - The tracking event to send (e.g., Purchase, AddToCart).
     * @param data - The specific payload for the event.
     */
    private fireEvent<EventType extends keyof TrackingPayloads>(event: EventType, data: TrackingPayloads[EventType] & TrackingPayload): void {
        if (!this.trackers || !this.trackers.length) {
            return;
        }

        this.trackers.forEach(tracker => {
            try {
                if (tracker.isActive()) {
                    tracker.fireEvent(event, data);
                }
            } catch (e) {
                this.logger.error(`Error in tracker "${tracker.constructor.name}" for event "${event}":`, e);
            }
        });
    }

    /**
     * Tracks a completed user registration.
     */
    public trackRegistrationCompleted(): void {
        this.fireEvent(TrackingEvent.CompleteRegistration, new CompleteRegistrationPayload("success"));
    }

    /**
     * Tracks a product added to the cart.
     * 
     * @param productId - ID of the added product
     * @param label - Name of the product
     * @param quantity - Quantity added
     * @param price - Single item price
     * @param currencySymbol - Currency symbol (€)
     * @param basketValue - Total basket value (no shipping)
     * @param cartIds - All product IDs in the cart
     */
    public trackAddToCart(productId: number, label: string, quantity: number, price: number, currencySymbol: string, basketValue: number, cartIds: number[]): void {
        if (!productId || quantity <= 0 || !price) return;

        this.fireEvent(TrackingEvent.AddToCart, new AddToCartPayload(
            productId,
            label,
            quantity,
            price,
            currencySymbol,
            cartIds,
            basketValue,
        ));
    }

    /**
     * Tracks the payment method selected during checkout.
     * 
     * @param orderId - Order identifier
     * @param items - List of items in the basket
     * @param currencySymbol - Currency symbol
     * @param basketValue - Total basket value (no shipping)
     * @param paymentMethod - The selected payment method (e.g., Cash, PayPal) (optional)
     * @param shipping - Shipping cost (optional)
     * @param orderType - Type of order (optional)
     */
    public trackPurchase(
        orderId: string,
        items: {
            product_id: number,
            count: number
        }[],
        currencySymbol: string,
        basketValue: number,
        shipping?: number,
        paymentMethod?: string,
        orderType?: string
    ): void {
        if (!items || !items.length) return;

        this.fireEvent(TrackingEvent.Purchase, new PurchasePayload(
            orderId,
            items.map(i => ({ id: i.product_id, quantity: i.count })),
            currencySymbol,
            basketValue,
            shipping,
            paymentMethod,
            orderType
        ));
    }


    /**
     * Tracks the payment method selected during checkout.
     * 
     * @param paymentMethod - The selected payment method (e.g., Cash, PayPal)
     */
    public trackPaymentInfo(paymentMethod: string): void {
        if (!paymentMethod) return;
        this.fireEvent(TrackingEvent.AddPaymentInfo, new AddPaymentInfoPayload(paymentMethod));
    }


    /**
     * Tracks when a user selects a store or location.
     * 
     * @param location - The selected location data
     */
    public trackFindLocation(location: LocationWebshopViewModel): void {
        if (!location || !location.zip || !location.street) return;

        this.fireEvent(TrackingEvent.FindLocation, new FindLocationPayload(
            location.zip,
            location.street
        ));
    }

    /**
     * Tracks when a user initiates the checkout process.
     * 
     * @param type - Type of checkout (e.g., "adyen")
     * @param items - List of items in the basket
     * @param currencySymbol - Currency symbol
     * @param basketValue - Total basket value (no shipping)
     */
    public trackInitiateCheckout(
        type: string,
        items: { product_id: number; count: number }[],
        currencySymbol: string,
        basketValue: number
    ): void {
        if (!items || items.length === 0 || basketValue <= 0) return;

        this.fireEvent(TrackingEvent.InitiateCheckout, new InitiateCheckoutPayload(
            type,
            items,
            currencySymbol,
            basketValue,
        ));
    }
}